import { AuthorisedSignatory } from '@models/clients/clients.details/signatoryRegime';
import { ClientCRMRepo } from '@services/clients/clientCRM.repo';
import { DropdownOptionsService } from '@services/clients/dropdownOptions.service';
import * as angular from 'angular';

export class AuthorisedSignatoryPanelController {
    public isOpen: boolean = false;
    public isEdit: boolean = false;
    public authorisedSignatory: AuthorisedSignatory = {};
    public errorMessage: string = '';
    public onSave: (authorisedSignatory: AuthorisedSignatory) => void;
    public onClose: () => void;
    public onDelete: (authorisedSignatory: AuthorisedSignatory) => void;
    public onReopenSignatoryRegime: () => void;
    public connections: any[] = [];
    public client: any;
    public signatoryRegimeId: number;
    public loading: boolean = true;
    public selectedConnection: any = null;
    public searchTextConnection: string = '';
    public dropdownOptions: any = {};
    /** @ngInject */
    constructor(
        private clientCRMRepo: ClientCRMRepo,
        private $mdDialog: angular.material.IDialogService,
        private $rootScope: angular.IRootScopeService,
        private dropdownOptionsService: DropdownOptionsService
    ) {}

    public $onInit(): void {
        this.resetForm();
        this.loadConnections();
        this.loadDropdownOptions();
    }

    public loadConnections(): void {
        this.loading = true;
        // Fetch all connections without clientId filter
        this.clientCRMRepo.getAllConnectionsContacts()
            .then((response: any) => {
                this.connections = (response.data || []).map((connection: any) => ({
                    id: connection.id,
                    displayName: `${connection.firstName || ''} ${connection.lastName || ''}`.trim() || connection.name || 'Unknown'
                }));
                this.loading = false;
            })
            .catch((error) => {
                console.error('Error loading connections:', error);
                this.connections = [];
                this.loading = false;
            });
    }

    public loadDropdownOptions(): void {
        this.dropdownOptionsService.getAllOptions()
            .then((options) => {
                this.dropdownOptions = options;
            })
            .catch((error) => {
                console.warn('Failed to get dropdown options:', error);
                this.dropdownOptions = {};
            });
    }

    public open(authorisedSignatory?: AuthorisedSignatory): void {
        this.isOpen = true;
        this.isEdit = !!authorisedSignatory;
        this.errorMessage = ''; // Clear any previous errors
        
        // Prevent body scroll
        document.body.classList.add('slide-out-panel-open');
        
        // Reload connections when opening the panel
        this.loadConnections();
        
        if (authorisedSignatory && typeof authorisedSignatory === 'object') {
            this.authorisedSignatory = { ...authorisedSignatory };
            // Set the selected connection if we have a name
            if (this.authorisedSignatory.name) {
                const foundConnection = this.connections.find((connection: any) => connection.id === this.authorisedSignatory.name);
                if (foundConnection) {
                    this.selectedConnection = foundConnection;
                    this.searchTextConnection = foundConnection.displayName;
                }
            }
        } else {
            this.resetForm();
        }
    }

    public close(): void {
        this.isOpen = false;
        this.errorMessage = ''; // Clear errors when closing
        
        // Restore body scroll
        document.body.classList.remove('slide-out-panel-open');
        
        if (this.onClose) {
            this.onClose();
        }
    }

    public filterConnections(searchText: string): any[] {
        if (!searchText) {
            return this.connections;
        }
        const lowerSearchText = searchText.toLowerCase();
        return this.connections.filter((connection: any) => 
            connection.displayName.toLowerCase().includes(lowerSearchText)
        );
    }

    public updateConnection(): void {        
        if (this.selectedConnection) {
            this.authorisedSignatory.name = this.selectedConnection.id;
            this.authorisedSignatory.nameValue = this.selectedConnection.displayName;
        }
    }

    public openAddConnectionModal(): void {
        const tempScope = this.$rootScope.$new(true) as any;
        tempScope.client = this.client;
        tempScope.connection = {};
        tempScope.dropdownOptions = this.dropdownOptions;

        this.$mdDialog.show({
            template: '<md-dialog flex="70"><add-connection-modal client="client" connection="connection" dropdown-options="dropdownOptions"></add-connection-modal></md-dialog>',
            scope: tempScope,
            clickOutsideToClose: true,
            escapeToClose: true
        }).then((result: any) => {
            if (result) {
                this.connectionSaved(result);
            }
        }).catch(() => {
            // Dialog was cancelled
        });
    }

    public onNameChange(): void {
        if (this.authorisedSignatory.name && this.connections.length > 0) {
            const foundConnection = this.connections.find((connection: any) => connection.id === this.authorisedSignatory.name);
            this.authorisedSignatory.nameValue = foundConnection ? foundConnection.displayName : '';
        } else {
            this.authorisedSignatory.nameValue = '';
        }
    }

    public save(): void {
        
        // Clear previous error messages
        this.errorMessage = '';
        
        // Validate required fields
        if (!this.authorisedSignatory || !this.authorisedSignatory.name) {
            this.errorMessage = 'Name is required. Please select a name before saving.';
            console.error('Name is required');
            return; // Don't save or close the panel
        }

        // Ensure authorisedSignatory is properly initialized
        if (!this.authorisedSignatory) {
            this.authorisedSignatory = {};
        }

        if (this.onSave) {
            (this.onSave as any)({ authorisedSignatory: { ...this.authorisedSignatory } });
        }
        
        this.close();
    }

    public delete(): void {
        if (this.authorisedSignatory && this.authorisedSignatory.id) {
            if (this.onDelete) {
                (this.onDelete as any)({ authorisedSignatory: { ...this.authorisedSignatory } });
            }
        }
        this.close();
    }

    private connectionSaved(result: any): void {
        if (result) {
            this.loading = true;
            // Save connection without client association (clientId is null)
            this.clientCRMRepo.saveConnectionContact(null, result)
                .then((response: any) => {
                    this.loading = false;
                    // Reload connections to include the newly saved one
                    this.loadConnections();
                    // Call the reopen callback to reopen the signatory regime modal
                    if (this.onReopenSignatoryRegime) {
                        this.onReopenSignatoryRegime();
                    }
                })
                .catch((error) => {
                    console.error('Error saving connection:', error);
                    this.loading = false;
                });
        }
    }

    private resetForm(): void {
        this.authorisedSignatory = {
            id: undefined,
            externalId: '',
            name: undefined,
            nameValue: ''
        };
        this.selectedConnection = null;
        this.searchTextConnection = '';
    }
}

export const AuthorisedSignatoryPanelComponent: angular.IComponentOptions = {
    template: require('./authorised-signatory-panel.html'),
    controller: AuthorisedSignatoryPanelController,
    controllerAs: '$ctrl',
    bindings: {
        client: '<',
        signatoryRegimeId: '<',
        onSave: '&',
        onClose: '&',
        onDelete: '&',
        onReopenSignatoryRegime: '&'
    }
};



// WEBPACK FOOTER //
// ./src/app/clients/clients.details/recipients/modals/tabs/authorised-signatory/authorised-signatory-panel.ts